import Textfield from '@atlaskit/textfield';
import { useFlaggingCallback } from '@databases/banners';
import { submitFormOnEnter } from '@databases/form/Form';
import ValidationStatus from '@databases/form/ValidationStatus';
import { Metrics } from '@databases/frontend-metrics';
import { useTypingPerformance } from '@databases/frontend-metrics/useTypingPerformance';
import { messages } from '@databases/intl/messages';
import { getTextDelta, useTextDelta } from '@databases/react/useTextDelta';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { useScope } from '@databases/scope';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  getDatabaseFieldRequired,
  getExistingFieldNames,
  getUniqueFieldName,
  isUniqueFieldName,
} from '@databases/utils/database/utils';
import composeRefs from '@seznam/compose-react-refs';
import { noop } from 'lodash-es';
import React, { forwardRef, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { FIELD_TYPES } from '../../field-types/fieldTypes';

export const FieldNameEditContainer = styled.div``;

export interface FieldNameEditProps {
  contentId: string;
  fieldId: string;
  showPlaceholder?: boolean;
  noValidation?: boolean;
  isFieldSettings?: boolean;
}

const FieldNameEdit = forwardRef<HTMLInputElement, FieldNameEditProps>(
  function FieldNameEdit(
    { contentId, fieldId, showPlaceholder, noValidation, isFieldSettings },
    ref
  ) {
    const scope = useScope();
    const { formatMessage } = useIntl();
    const store = defaultStoreUtils.useStore();

    const [field, validate] = defaultStoreUtils.useEqualSelector((state) => {
      const databaseDraft = selectDatabaseDraftByContentId(state, {
        contentId,
        scope,
        includeDeleted: true,
      });
      const field = getDatabaseFieldRequired(databaseDraft, fieldId);

      const validate = noValidation
        ? true
        : isUniqueFieldName(field.name, getExistingFieldNames(databaseDraft, field));
      return [field, validate] as const;
    });

    const dispatch = defaultStoreUtils.useDispatch();
    const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
    useTextDelta(
      inputRef,
      useCallback(
        (delta, newValue) => {
          dispatch(
            addChangeToDraft(contentId, scope, {
              type: DatabaseChangeType.SET_FIELD_NAME,
              fieldId,
              name: newValue,
              delta,
            })
          );
        },
        [contentId, dispatch, fieldId, scope]
      )
    );

    const setUniqueFieldName = useCallback(
      (value: string) => {
        const databaseDraft = selectDatabaseDraftByContentId(store.getState(), {
          contentId,
          scope,
          includeDeleted: true,
        });
        const fieldLabel = formatMessage(
          messages['field-settings.field-name-inline-edit-default-text'],
          {
            defaultFieldName: formatMessage(FIELD_TYPES[field.type].label),
          }
        );
        const uniqueFieldName = getUniqueFieldName(
          value === '' ? fieldLabel : value,
          getExistingFieldNames(databaseDraft, field)
        );

        dispatch(
          addChangeToDraft(contentId, scope, {
            type: DatabaseChangeType.SET_FIELD_NAME,
            fieldId,
            name: uniqueFieldName,
            delta: getTextDelta(field.name, uniqueFieldName, field.name.length),
          })
        );
      },
      [contentId, dispatch, field, fieldId, formatMessage, scope, store]
    );

    const handleKeyDown = useFlaggingCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => submitFormOnEnter(e),
      []
    );

    const handleFocus = useFlaggingCallback((e: React.FocusEvent<HTMLInputElement>) => {
      // Put cursor to the end of the field
      e.currentTarget.setSelectionRange(
        e.currentTarget.value.length,
        e.currentTarget.value.length
      );
    }, []);

    const handleBlur = useCallback(() => {
      setUniqueFieldName(field.name.trim());
    }, [field.name, setUniqueFieldName]);

    const typingPerformanceRef = useTypingPerformance<HTMLInputElement>(
      Metrics.MEAN_FIELD_NAME_TYPING
    );

    return (
      <ValidationStatus
        error={
          validate ? undefined : formatMessage(messages['validation.unique-field-name'])
        }
      >
        {(props) => (
          <FieldNameEditContainer>
            <Textfield
              {...props}
              autoComplete="off"
              placeholder={
                isFieldSettings
                  ? formatMessage(messages['field-settings.field-name-placeholder'])
                  : showPlaceholder
                  ? formatMessage(messages['field.default-field-name'])
                  : undefined
              }
              value={field?.name}
              onChange={noop}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              size={1}
              ref={composeRefs(ref, setInputRef, typingPerformanceRef)}
            />
          </FieldNameEditContainer>
        )}
      </ValidationStatus>
    );
  }
);

export default FieldNameEdit;
